import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import { Button } from '@mui/material'

import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { viewPdfDialogStyles } from './view-pdf-dialog.styles'

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

/**
 * @typedef {object} ViewPDFDialogProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {function(): void} [close] - Función para cerrar el diálogo.
 * @property {string | number} [docId] - ID del documento, usado para marcar documentos como vistos en modo múltiple.
 * @property {string | File} file - URL o archivo PDF a mostrar.
 * @property {function(boolean): void} setDisabled - Función para habilitar/deshabilitar controles externos.
 * @property {function(string | number): void} [setMultipleArray] - Función para agregar el documento a un array de documentos vistos.
 * @property {boolean} [shortcutEnabled=false] - Si es `true`, muestra un botón para saltar a la última página.
 */

/**
 * Componente de visualizador de PDF con navegación página por página.
 *
 * Proporciona un visor de PDF interactivo con:
 * - **Navegación por páginas**: Botones para avanzar/retroceder
 * - **Indicador de carga**: Muestra mensajes mientras carga el PDF y las páginas
 * - **Atajo a última página**: Opción para saltar directamente a la última página
 * - **Integración con formularios**: Marca documentos como vistos al completar la visualización
 * - **Internacionalización**: Textos de botones traducidos
 * - **Manejo de errores**: Captura y registra errores de carga
 *
 * El componente utiliza react-pdf para renderizar documentos PDF y proporciona
 * una experiencia de navegación secuencial, útil para revisión de documentos
 * que requieren confirmación de lectura.
 *
 * @component
 * @example
 * const MyDocumentViewer = () => {
 *   const [disabled, setDisabled] = useState(true);
 *   const [viewedDocs, setViewedDocs] = useState([]);
 *
 *   const handleDocViewed = (docId) => {
 *     setViewedDocs([...viewedDocs, docId]);
 *   };
 *
 *   return (
 *     <ViewPDFDialog
 *       file="/documents/terms.pdf"
 *       docId="terms-001"
 *       setDisabled={setDisabled}
 *       setMultipleArray={handleDocViewed}
 *       shortcutEnabled={true}
 *       close={() => console.log('Dialog closed')}
 *     />
 *   );
 * };
 *
 * @param {ViewPDFDialogProps} props - Las props del componente ViewPDFDialog.
 */
export const ViewPDFDialog = withStyles(viewPdfDialogStyles)(({
  classes,
  close = () => {},
  docId = null,
  file,
  setDisabled,
  setMultipleArray = null,
  shortcutEnabled = false,
}) => {
  const { formatMessage } = useIntl()
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [isLoading, setIsLoading] = useState(true)

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
  }

  const handleNextPage = () => {
    setIsLoading(true)
    if (pageNumber < numPages) setPageNumber(pageNumber + 1)
    else {
      if (!docId) {
        setDisabled(false)
        close()
      } else {
        setMultipleArray(docId)
        close()
      }
    }
  }

  const handlePrevPage = () => {
    setIsLoading(true)
    if (pageNumber > 1) setPageNumber(pageNumber - 1)
    else {
      close()
    }
  }

  const handleLastPage = () => {
    setIsLoading(true)
    setPageNumber(numPages)
  }

  return (
    <>
      <Document
        file={file}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={(error) => {
          console.error('Error loading PDF:', error)
          setIsLoading(false)
        }}
        className={classes.pdfWrapper}
        loading={<div>Cargando PDF...</div>}
      >
        <Page
          pageNumber={pageNumber}
          scale={1.2}
          onLoadSuccess={() => setIsLoading(false)}
          onLoadError={(error) => {
            console.error('Error loading page:', error)
            setIsLoading(false)
          }}
          loading={<div>Cargando página...</div>}
        />
      </Document>
      {!isLoading ? (
        <div className={classes.pdfButtonsWrapper}>
          <Button
            variant="contained"
            color="primary"
            className={classes.pdfButton}
            onClick={handlePrevPage}
          >
            {pageNumber === 1
              ? formatMessage({ id: 'global.close' })
              : formatMessage({ id: 'global.previous' })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.pdfButton}
            onClick={handleNextPage}
          >
            {pageNumber === numPages
              ? formatMessage({ id: 'global.accept' })
              : formatMessage({ id: 'global.next' })}
          </Button>
          {shortcutEnabled && pageNumber !== numPages ? (
            <Button
              variant="contained"
              color="secondary"
              className={classes.pdfButton}
              onClick={handleLastPage}
            >
              {formatMessage({ id: 'global.goLastPage' })}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  )
})
